/**
 * AudioTelemetryBuffer — the small dedicated context ring.
 *
 * The noisy low-level audio sources (page lifecycle, element events,
 * browser-mute, livekit logs) write here on every transition instead of
 * emitting telemetry directly — they fire constantly during healthy play. When
 * the dead-air detector confirms an episode, it serializes a slice of this ring
 * into the Sentry episode bundle so we can see *why* the client went deaf.
 *
 * Why a dedicated ring rather than Sentry breadcrumbs alone: breadcrumbs are a
 * 100-entry global ring shared with console/fetch/navigation noise, so the
 * audio history gets evicted before an episode fires. This ring is audio-only
 * and time-windowed, so it cannot be crowded out — and writing the same events
 * as breadcrumbs too would recreate the eviction it exists to avoid.
 *
 * Bounds: time-windowed (default ~30s) and hard-capped (default ~150 entries)
 * as a runaway-burst backstop, so the stringified slice stays well under
 * Sentry's normalization and ~1 MB event limits. Both bounds are configurable.
 */

export interface AudioRingEntry {
  /** Epoch ms when the entry was recorded. */
  t: number
  /** Which context source produced it (e.g. 'audiocontext', 'lifecycle'). */
  source: string
  /** The transition/event name (e.g. 'statechange', 'hidden', 'mute'). */
  event: string
  /** Small structured payload; kept shallow for cheap serialization. */
  data?: Record<string, unknown>
}

export const AUDIO_RING_WINDOW_MS = 30_000
export const AUDIO_RING_MAX_ENTRIES = 150

export interface AudioTelemetryBufferOptions {
  windowMs?: number
  maxEntries?: number
  /** Injectable clock (defaults to Date.now). */
  now?: () => number
}

export class AudioTelemetryBuffer {
  private entries: AudioRingEntry[] = []
  private readonly windowMs: number
  private readonly maxEntries: number
  private readonly now: () => number

  constructor(options: AudioTelemetryBufferOptions = {}) {
    this.windowMs = options.windowMs ?? AUDIO_RING_WINDOW_MS
    this.maxEntries = options.maxEntries ?? AUDIO_RING_MAX_ENTRIES
    this.now = options.now ?? Date.now
  }

  /**
   * Record a context transition. Stamps `t` from the clock, evicts entries
   * older than the window, then enforces the hard cap (drop-oldest).
   */
  push(source: string, event: string, data?: Record<string, unknown>): void {
    const t = this.now()
    this.entries.push({ t, source, event, data })
    this.evict(t)
  }

  private evict(nowT: number): void {
    const cutoff = nowT - this.windowMs
    // Drop out-of-window entries. Entries are pushed in time order, so the
    // stale ones are always a prefix.
    let firstFresh = 0
    while (
      firstFresh < this.entries.length &&
      this.entries[firstFresh].t < cutoff
    ) {
      firstFresh++
    }
    if (firstFresh > 0) this.entries.splice(0, firstFresh)
    // Runaway-burst backstop: if a single window somehow exceeds the cap
    // (many transitions in <30s), keep only the most recent maxEntries.
    if (this.entries.length > this.maxEntries) {
      this.entries.splice(0, this.entries.length - this.maxEntries)
    }
  }

  /** Current in-window entries (a copy; evicts stale first). */
  slice(): AudioRingEntry[] {
    this.evict(this.now())
    return [...this.entries]
  }

  /**
   * The ring as a JSON string for the Sentry `audioRing` context field.
   * Stringifying sidesteps Sentry's `normalizeDepth: 3` (which would flatten
   * nested `data`) and is immune to the 250-char `maxValueLength` that only
   * truncates message/exception/url, not contexts.
   *
   * A cycle or a BigInt anywhere in `data` makes `JSON.stringify` throw. This
   * is called from `AudioEpisodeSink.emitSentryPeers`, which is guarded, so a
   * throw would not break anything — it would silently drop the entire Sentry
   * bundle for the episode, and for every episode until the offending entry
   * left the window. One bad payload from a future writer is not worth the
   * durable half of the diagnosis, so the failure is contained per entry.
   */
  serialize(): string {
    try {
      return JSON.stringify(this.slice())
    } catch {
      return this.serializeEntrywise()
    }
  }

  /**
   * Failure path for {@link serialize}: render each entry alone and replace
   * only the ones that throw. The neighbouring entries are the context that
   * explains the episode, which is exactly what an all-or-nothing fallback
   * would throw away at the moment it is needed.
   *
   * The marker cannot throw in turn: `data` is the only field a caller fills
   * with arbitrary values, and it is the one field the marker drops.
   */
  private serializeEntrywise(): string {
    const parts = this.slice().map((entry) => {
      try {
        return JSON.stringify(entry)
      } catch {
        return JSON.stringify({
          t: entry.t,
          source: entry.source,
          event: entry.event,
          data: { error: 'serialize_failed' },
        })
      }
    })
    return `[${parts.join(',')}]`
  }
}
